import { useLang } from '@/i18n';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { getAPIData } from './api';
import { ItfAPIParams, ItfNewsItem, ItfTagTab } from './type';
import css from './index.module.scss';
import { transformData } from './util';
import EcoPagination from 'eco-design/base/eco-pagination';
import Card from './Card';

const US: React.FC = () => {
    const { locale = '' } = useRouter();
    const { t } = useLang('');
    const [params, setParams] = useState<ItfAPIParams | null>(null);
    const [lists, setLists] = useState<ItfNewsItem[]>([]);
    const [items, setItems] = useState<ItfNewsItem[]>([]);
    const [page, setPage] = useState<number>(1);
    const [total, setTotal] = useState<number>(0);
    const [style, setStyle] = useState({ l: 0, w: 0, t: 'none' });
    const ulRef = useRef<HTMLUListElement>(null);
    const liRefs = useRef<(HTMLLIElement | null)[]>([]);
    const tabs: ItfTagTab[] = [
        {
            label: t('tab1'),
            value: 'website-news',
        },
        {
            label: t('tab2'),
            value: 'csr-news',
        },
        {
            label: t('tab3'),
            value: 'partnership-news',
        },
    ];
    const [active, setActive] = useState<ItfTagTab>(tabs[0]);
    const initS1 = async () => {
        try {
            const { items } = await getAPIData(
                {
                    size: 4,
                    page: 1,
                },
                locale,
            );
            setLists(transformData(items, ''));
        } catch (e: any) {
            throw new Error(e);
        }
    };

    const initS2 = async () => {
        if (params) {
            try {
                const { items, total } = await getAPIData(params, locale);
                setTotal(total || 0);
                setItems(transformData(items, active.value) || ([] as ItfNewsItem[]));
            } catch (e: any) {
                throw new Error(e);
            }
        }
    };

    const doResetStyle = (i?: number | undefined) => {
        const li = liRefs.current[i || 0];
        const ul = ulRef.current;
        if (li && ul) {
            const liWidth = li.offsetWidth;
            setStyle({
                l: li.offsetLeft,
                w: liWidth,
                t: typeof i === 'number' ? 'left 0.4s ease' : 'none',
            });
        }
    };

    const handleChangeTab = (e: React.MouseEvent<HTMLLIElement>, tab: ItfTagTab, i: number) => {
        setActive(tab);
        if (page !== 1) {
            setPage(1);
        }
        e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        doResetStyle(i);
    };

    useEffect(() => {
        initS1();
        doResetStyle();
    }, [locale]);

    useEffect(() => {
        initS2();
    }, [params, locale]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const size = window?.innerWidth > 1024 ? 12 : window?.innerWidth >= 768 ? 6 : 5;
            setParams({ size, page, categoryId: active.value });
        }
    }, [active, page]);

    return (
        <main className={`news-page ${css['news-page']}`}>
            <section className="news-A">
                <h2 className="title">{t('title')}</h2>
                <div className="content">
                    <div className="first">{lists?.[0] && <Card card={lists[0]} />}</div>
                    <div className="lists">
                        {lists?.slice(1)?.map((list: ItfNewsItem, i: number) => (
                            <Card card={list} key={i} />
                        ))}
                    </div>
                </div>
            </section>
            <section className="news-B">
                <ul
                    ref={ulRef}
                    className="tabs"
                    style={{ '--tab-left': style.l + 'px', '--tab-width': style.w + 'px', '--tab-trans': style.t } as React.CSSProperties}
                >
                    {tabs?.map((tab: ItfTagTab, i: number) => (
                        <li
                            className={`tab ${active.value === tab?.value ? 'active' : 'deactive'}`}
                            ref={(el) => (liRefs.current[i] = el)}
                            key={i}
                            onClick={(e: React.MouseEvent<HTMLLIElement>) => handleChangeTab(e, tab, i)}
                        >
                            {tab.label}
                        </li>
                    ))}
                </ul>
                <div className={`items type-${active.value}`}>
                    {items?.map((item: ItfNewsItem, i: number) => (
                        <Card card={item} key={i} />
                    ))}
                </div>
                {total > (params?.size || 0) && (
                    <EcoPagination total={total} onChange={(page) => setPage(page)} current={params?.page || 1} defaultPageSize={params?.size || 0} />
                )}
            </section>
            <section className="news-C">
                <p className="line"></p>
                <h3 className="title">{t('contact_us')}</h3>
                <a className="text" href={`mailto:${t('mail')}`}>
                    <span>{t('mail')}</span>
                    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.5 5.5L15 12L8.5 18.5" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </a>
            </section>
            <style global>
                {`
                    .container {
                        width: 100%;
                    }
                `}
            </style>
        </main>
    );
};

export default US;
